import { CompoundName } from 'services/medications/medications.types';
import { PharmacyItem } from 'services/pharmacies/pharmacies.types';
import { LabsStatus } from 'services/wm/wm.types';

import { GenericEligibilityCheckResponse } from 'hooks/usePaywall/usePaywall.types';
import { WMDeviceFlow } from 'models/programs.types';
import {
  EmailStatus,
  SemaglutideMigrationPatientAction,
  SemaglutideMigrationPatientGroup,
  TirzepatideMigrationPatientAction,
  TirzepatideMigrationPatientGroup
} from 'utils/enums';

import { DocumentItem } from './document.types';

export enum Gender {
  Man = '0',
  NonBinary = '4',
  NotListed = '6',
  PreferToHide = '7',
  TransgenderMan = '2',
  TransgenderWoman = '3',
  TwoSpirit = '5',
  Unknown = '8',
  Woman = '1'
}

export enum SexAtBirth {
  Female = '1',
  Male = '0',
  Other = '2',
  Unknown = '3'
}

interface HealthCondition {
  name: string;
  notes: string;
}

interface ChronicDisease {
  name: string;
  notes: string;
}

interface SocialHistory {
  name: string;
  useFrequency?: string;
}

interface Medications {
  prescription: [];
  supplements: [];
}
export type EmailStatusType = `${EmailStatus}`;

interface OnboardingTask {
  _id: string;
  category: string;
  status: string;
}

interface PaymentProfile {
  _id: string;
  chargifyPaymentProfileId: string;
  expirationMonth: string;
  expirationYear: string;
  isDefault: boolean;
  paymentMethod: string;
}

interface PlanRecommendation {
  planRecommendationId: string | null;
}

export type FeatureStatusType = 'disabled' | 'enabled' | 'paywall';
interface FeatureProps {
  status: FeatureStatusType;
}
export interface Features {
  addToCalendarOnAppointmentCards?: FeatureProps;
  aiAssistantChat: FeatureProps;
  annual45: FeatureProps;
  choosingDoctor: FeatureProps;
  clickToCall: FeatureProps;
  complimentaryMedications: FeatureProps;
  documentsAndLinks: FeatureProps;
  frontDesk: FeatureProps;
  healthProfile: FeatureProps;
  labsAndImaging: FeatureProps;
  medicalRecords: FeatureProps;
  messaging: FeatureProps;
  migrateToBundlePlan: FeatureProps;
  myProvider: FeatureProps;
  noShowCharge?: FeatureProps;
  notifications: FeatureProps;
  offerAsyncAppointments: FeatureProps;
  prescriptionDiscountCard: FeatureProps;
  prescriptions: FeatureProps;
  reOnboardingWM: FeatureProps;
  scheduleAppointment: FeatureProps;
  shopRecurring?: FeatureProps;
  store: FeatureProps;
  writeMessage:
    | {
        status: 'disabled';
      }
    | {
        status: 'enabled';
      }
    | {
        action: GenericEligibilityCheckResponse['data']['action'];
        status: 'paywall';
      };
}

interface GlobalSetting {
  _id: string;
  uid: string;
  value: number | string;
}

interface MedicalIntake {
  allergyList: string[];
  chronicDiseases: ChronicDisease[];
  healthConditions: HealthCondition[];
  medications: Medications;
  socialHistory: SocialHistory[];
  womenHealth: { name: string };
}

export type PartnerNameType = 'Optavia' | 'HealthWarehouse' | 'HRT' | 'Ballad Health' | 'Blue Line';

export interface OnboardingPartnerProps {
  partnerName: PartnerNameType;
}

interface NotificationPreferences {
  browserNotification: {
    appointmentChanges: boolean;
    appointmentReminder: boolean;
    messages: boolean;
    newOffers: boolean;
  };
  emailNotification: {
    appointmentChanges: boolean;
    appointmentReminder: boolean;
    messages: boolean;
    newOffers: boolean;
  };
  textNotification: {
    appointmentChanges: boolean;
    appointmentReminder: boolean;
    messages: boolean;
    newOffers: boolean;
  };
}
interface UserType {
  name: string;
  shortCode: string;
}
export interface PhoneProps {
  countryCode: string;
  countryPrefix: string;
  internationalFormat: string;
  phoneNumber: string;
}

interface BodyImage {
  documents: DocumentItem[];
  manualStatus: 'accepted' | 'invalid' | null;
  manualStatusUpdatedAt: Date;
  manualStatusUpdatedBy: string;
  uploadRequired: boolean;
}

type TirzepatideMigrationEligibility = 'eligible' | 'na' | 'not-eligible';

type TirzepatideMigrationEligibilityType =
  'not-eligible-care-only' | 'not-eligible-bundled' | 'not-eligible-maintenance' | 'eligible';

export interface TirzepatideMigrationProps {
  eligibility: TirzepatideMigrationEligibility;
  eligibilityType: TirzepatideMigrationEligibilityType;
  eligibleMedications?: string[];
  mifId: string;
  mifSubmittedAt: Date;
  patientAction: TirzepatideMigrationPatientAction;
  patientActionSubmittedAt: Date;
  patientGroup: TirzepatideMigrationPatientGroup;
  renewalSurveyCompletedAt: Date;
  renewalSurveyId: string;
}

type SemaglutideMigrationEligibility = 'eligible' | 'na' | 'not-eligible';

type SemaglutideMigrationEligibilityType =
  'not-eligible-care-only' | 'not-eligible-bundled' | 'not-eligible-maintenance' | 'eligible';

export interface SemaglutideMigrationProps {
  eligibility: SemaglutideMigrationEligibility;
  eligibilityType: SemaglutideMigrationEligibilityType;
  eligibleMedications?: CompoundName[];
  mifId: string;
  mifSubmittedAt: Date;
  patientAction: SemaglutideMigrationPatientAction;
  patientActionSubmittedAt: Date;
  patientGroup: SemaglutideMigrationPatientGroup;
  renewalSurveyCompletedAt: Date;
  renewalSurveyId: string;
}

export interface UserProfile {
  _id: string;
  aboutMe?: string;
  accessToken: string;
  address: string;
  address2?: string;
  agreements?: {
    asyncMedicareBeneficiaryAgreedTime?: Date;
    hipaaMarketingAgreed?: boolean;
    lifemdOnboardingTermsV2AgreedTime?: Date;
    lifemdPharmacyTerms?: {
      agreedAt?: string;
      source?: string;
      status?: 'pending' | 'accepted' | 'declined';
      triggeredAt?: string;
    };
    novocareTermsAgreedTime?: Date;
    selfPayMedicareAgreedTime?: Date;
    videoCallRecordingAgreedTime?: Date;
  };
  apartmentUnit?: string;
  availableReview: boolean;
  bodyImage?: BodyImage;
  brazeExternalId: string;
  /**
   * Bridge (Cloud Health Medical Group) patient ID. Populated by the
   * backend `BRIDGE_CREATE_PATIENT` worker (LMD-25651) during signup for
   * users routed to the Bridge billing path. Optional — absent for
   * non-Bridge users and for Bridge-routed users whose worker hasn't
   * completed yet. Downstream Bridge-aware flows (insurance update,
   * re-consent) key off its presence.
   */
  bridgePatientId?: string;
  changePassword: { expiresAt?: Date; sentAt: Date } | null;
  city: string;
  companyPartners: {
    connectedPartners: OnboardingPartnerProps[] | null;
    onboardingPartner: OnboardingPartnerProps | null;
  };
  connectedWMDevices?: ConnectedWMDevice[];
  crosscheckVerified?: boolean; // undefined means not performed yet
  customerId: string;
  dob: string;
  doctorId: string;
  dynamicLink: string;
  elationId: string;
  email: string;
  emailStatus: EmailStatusType;
  expiredToken?: string;
  features?: Features;
  firstName: string;
  flowUrl?: string;
  gender?: Gender | '';
  globalSettings: GlobalSetting[];
  hasActiveOrders: number;
  hasActiveResults: number;
  hasInsurance: boolean;
  hasPassword?: boolean;
  hasPasswordResetToken: boolean;
  /**
   * @deprecated This property will be removed in a future version.
   */
  hasSubscription: boolean;
  identityVerified: boolean;
  identityVerifiedAt: string | null;
  initialLoginVideo: string;
  isAsyncSignup?: boolean;
  isInitialLogin: boolean;
  isStaff?: boolean;
  isVerifiedByVouched: boolean | null;
  isVerifyingPending?: boolean;
  isWMDevices?: boolean;
  labsStatus?: LabsStatus;
  lastLogin: string;
  lastLoginClientPlatform: string;
  lastName: string;
  medicalIntake?: MedicalIntake;
  miCompletionStatus?: boolean; // temporary bring back as a fallback for users w/o program with miStatus
  nextSubscriptionRenewalAt?: string | null; // used for preferred medication
  nickName?: string;
  notificationPreferences?: NotificationPreferences;
  novocareTermsStatus?: 'pending' | 'accepted' | 'declined';
  /**
   * Whether the patient has completed checkout at least once (Epic 1933).
   * `false` = blank signup (never paid); `true` / undefined = default post-checkout.
   */
  onboardingComplete?: boolean;
  // show banner only for 'pending'
  onboardingTask: OnboardingTask;
  paymentProfiles: PaymentProfile[];
  pendingEmail?: string;
  pharmacy: PharmacyItem;
  phone: PhoneProps;
  planRecommendation?: PlanRecommendation;
  profileImage?: string;
  refreshToken: string;
  registrationDate: string;
  reviewedProviders: string[];
  semaglutideMigration?: SemaglutideMigrationProps;
  sexAtBirth: SexAtBirth | '';
  state: string;
  status: string;
  subscriptionId: string;
  timezone: string;
  tirzepatideMigration?: TirzepatideMigrationProps;
  useNewPostOnboarding?: boolean;
  userId: string;
  userType?: UserType;
  verifyEmail: { expiresAt?: Date; sentAt: Date } | null;
  wmDeviceFlow?: WMDeviceFlow;
  zipCode: string;
}

export type MailingAddress = {
  address: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
};

export type UserCurrentWMPPType = 'care-only' | 'bundle' | 'maintenance';

interface ConnectedWMDevice {
  createdAt: string;
  device: {
    macAddress?: string;
    model?: string;
    modelId?: string;
    name: string;
    type: string;
  };
  updatedAt: string;
}
